/**
 * Week subsection — a single disease × type section (one of the 12
 * inside a WeekView). Renders:
 *
 *   ┌─ type heading (e.g. "临床研究") ─────────────────────┐
 *   ├─ review paragraphs                                    │
 *   ├─ included items table                                 │
 *   └─ (empty state if no items + empty_note)               │
 *
 * The subsections are themselves composed inside `weekSection.ts`.
 */

import { el } from "../dom";
import { reviewParagraphs } from "./reviewParagraphs";
import { includedItemsTable } from "./includedItemsTable";
import type { WeekIncludedItem, WeekSection } from "../types";

export interface WeekSubsectionOptions {
  /** Section metadata from `summaries` rows. */
  section: WeekSection;
  /** Items that belong to this disease × type. */
  items: WeekIncludedItem[];
}

export function weekSubsection({ section, items }: WeekSubsectionOptions): HTMLElement {
  const root = el("section", {
    class: "week-subsection",
    "data-disease": section.disease_key,
    "data-type": section.type_label,
  }, [
    el("h4", { class: "week-subsection-title" }, section.type_label),
  ]);

  // Review paragraphs always render (even when empty) so the
  // structure stays consistent across the 12 sections.
  root.appendChild(reviewParagraphs({ paragraphs: section.paragraphs ?? [] }));

  // Empty section: show the curated `empty_note` instead of the
  // table. Worker surfaces this as null when at least one item is
  // included.
  if (items.length === 0) {
    const note =
      section.empty_note ?? `本周该疾病无 ${section.type_label} 文献纳入`;
    root.appendChild(
      el("div", { class: "week-subsection-empty" }, [
        el("span", { class: "week-subsection-empty-icon", "aria-hidden": "true" }, "⌀"),
        el("span", { class: "week-subsection-empty-text" }, note),
      ]),
    );
    return root;
  }

  root.appendChild(includedItemsTable({ items }));
  return root;
}